import { useState, useEffect } from 'react';
import { goalsService, Goal, GoalStats } from '../services/goalsService';
import PageHeader from '../components/PageHeader';
import { Card } from '../components/ui/Card';
import CreateGoalModal from '../components/goals/CreateGoalModal';
import GoalProgressChart from '../components/goals/GoalProgressChart';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import {
  TrendingDown,
  Dumbbell,
  Scale,
  Activity,
  Heart,
  Pill,
  Move,
  Thermometer,
  Target,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Calendar,
  CheckCircle2,
  TrendingUp,
  Sparkles
} from 'lucide-react';

// Values must match Prisma GoalType enum exactly
const GOAL_TYPES: Record<string, { label: string; icon: any; color: string; gradient: string }> = {
  WEIGHT_LOSS: { 
    label: 'Slăbire', 
    icon: TrendingDown, 
    color: 'bg-red-50 text-red-700 border-red-200',
    gradient: 'from-red-500 to-pink-500'
  },
  MUSCLE_GAIN: { 
    label: 'Masă Musculară', 
    icon: Dumbbell, 
    color: 'bg-blue-50 text-blue-700 border-blue-200',
    gradient: 'from-blue-500 to-indigo-500'
  },
  MAINTAIN_WEIGHT: { 
    label: 'Menținere Greutate', 
    icon: Scale, 
    color: 'bg-gray-50 text-gray-700 border-gray-200',
    gradient: 'from-gray-500 to-slate-500'
  },
  START_EXERCISE: { 
    label: 'Fitness & Sport', 
    icon: Activity, 
    color: 'bg-green-50 text-green-700 border-green-200',
    gradient: 'from-green-500 to-emerald-500'
  },
  REDUCE_JOINT_PAIN: { 
    label: 'Reducere Dureri', 
    icon: Heart, 
    color: 'bg-purple-50 text-purple-700 border-purple-200',
    gradient: 'from-purple-500 to-violet-500'
  },
  CONTROL_CHRONIC_PAIN: { 
    label: 'Control Durere Cronică', 
    icon: Pill, 
    color: 'bg-pink-50 text-pink-700 border-pink-200',
    gradient: 'from-pink-500 to-rose-500'
  },
  REGAIN_MOBILITY: { 
    label: 'Mobilitate', 
    icon: Move, 
    color: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    gradient: 'from-indigo-500 to-blue-500'
  },
  PAIN_MANAGEMENT: { 
    label: 'Management Durere', 
    icon: Thermometer, 
    color: 'bg-orange-50 text-orange-700 border-orange-200',
    gradient: 'from-orange-500 to-amber-500'
  },
  // Additional valid Prisma enum values (for display of existing goals)
  HEALTHY_LIFESTYLE: { label: 'Stil de Viață Sănătos', icon: Activity, color: 'bg-teal-50 text-teal-700 border-teal-200', gradient: 'from-teal-500 to-green-500' },
  REDUCE_BELLY_FAT: { label: 'Reducere Grăsime Abdominală', icon: TrendingDown, color: 'bg-orange-50 text-orange-700 border-orange-200', gradient: 'from-orange-500 to-red-500' },
  INCREASE_ENERGY: { label: 'Energie Zilnică', icon: Activity, color: 'bg-yellow-50 text-yellow-700 border-yellow-200', gradient: 'from-yellow-500 to-orange-500' },
  BETTER_SLEEP: { label: 'Somn Mai Bun', icon: Heart, color: 'bg-blue-50 text-blue-700 border-blue-200', gradient: 'from-blue-500 to-purple-500' },
  IMPROVE_BALANCE: { label: 'Echilibru', icon: Move, color: 'bg-cyan-50 text-cyan-700 border-cyan-200', gradient: 'from-cyan-500 to-blue-500' },
  REDUCE_CHRONIC_FATIGUE: { label: 'Reducere Oboseală', icon: Activity, color: 'bg-purple-50 text-purple-700 border-purple-200', gradient: 'from-purple-500 to-pink-500' },
};

export default function GoalsPage() {
  const { user } = useAuth();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [stats, setStats] = useState<GoalStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'active' | 'completed'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [expandedCharts, setExpandedCharts] = useState<Set<string>>(new Set());

  // Goal templates - Simple suggestions that match the form
  const getGoalTemplates = () => {
    if (!user?.healthGoal) return [];

    // Simple templates matching modal GOAL_TYPES (must use valid Prisma GoalType enum values)
    const templates: Record<string, Array<{ goalType: string; title: string; description: string }>> = {
      // HEALTHY MODE
      WEIGHT_LOSS: [
        { goalType: 'WEIGHT_LOSS', title: 'Slăbește 5kg', description: 'Pierdere sănătoasă în 2-3 luni' },
        { goalType: 'START_EXERCISE', title: 'Alergă 5km', description: 'Construiește rezistență cardio' }
      ],
      MUSCLE_GAIN: [
        { goalType: 'MUSCLE_GAIN', title: 'Câștigă 3kg masă', description: 'Creștere progresivă musculară' },
        { goalType: 'START_EXERCISE', title: 'Antrenament 4x/săpt', description: 'Forță și hipertrofie' }
      ],
      MAINTAIN_WEIGHT: [
        { goalType: 'MAINTAIN_WEIGHT', title: 'Mențin greutatea', description: 'Echilibru caloric stabil' },
        { goalType: 'START_EXERCISE', title: 'Îmbunătățesc condiția', description: 'Fitness general fără schimbare greutate' }
      ],
      
      // CHRONIC MODE
      REDUCE_JOINT_PAIN: [
        { goalType: 'PAIN_MANAGEMENT', title: 'Reduc durerea articulară', description: 'De la 7/10 la 4/10' },
        { goalType: 'REGAIN_MOBILITY', title: 'Îmbunătățesc mobilitatea', description: 'Stretching și exerciții blânde' }
      ],
      CONTROL_CHRONIC_PAIN: [
        { goalType: 'PAIN_MANAGEMENT', title: 'Gestionez durerea', description: 'Jurnal zilnic și tehnici relaxare' },
        { goalType: 'REDUCE_JOINT_PAIN', title: 'Reduc simptomele', description: 'Management holistic al durerii' }
      ],
      REDUCE_INFLAMMATION: [
        { goalType: 'REDUCE_JOINT_PAIN', title: 'Reduc inflamația', description: 'Dietă antiinflamatoare' },
        { goalType: 'CONTROL_CHRONIC_PAIN', title: 'Respect tratamentul', description: 'Iau medicația zilnic' }
      ]
    };

    return templates[user.healthGoal] || [];
  };

  useEffect(() => {
    loadData();
  }, [filter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [goalsData, statsData] = await Promise.all([
        goalsService.getGoals(
          filter === 'all' ? {} :
          filter === 'active' ? { isActive: true, isCompleted: false } :
          { isCompleted: true }
        ),
        goalsService.getGoalStats(),
      ]);
      setGoals(goalsData || []);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading goals:', error);
      setGoals([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGoal = async (goalId: string) => {
    if (!confirm('Sigur vrei să ștergi acest obiectiv?')) return;
    
    try {
      await goalsService.deleteGoal(goalId);
      toast.success('Obiectiv șters cu succes!', {
        icon: '🗑️',
        duration: 3000,
      });
      loadData();
    } catch (error) {
      console.error('Error deleting goal:', error);
      toast.error('Eroare la ștergerea obiectivului', {
        duration: 4000,
      });
    }
  };

  const handleUpdateProgress = async (goalId: string, newValue: number) => {
    try {
      await goalsService.updateGoal(goalId, { currentValue: newValue });
      toast.success('Progres actualizat cu succes!', {
        icon: '🎯',
        duration: 3000,
      });
      loadData();
    } catch (error) {
      console.error('Error updating goal:', error);
      toast.error('Eroare la actualizarea progresului', {
        duration: 4000,
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        <PageHeader title="Obiectivele Mele" />
        <div className="p-4">
          <div className="text-center py-8">Se încarcă...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <PageHeader title="Obiectivele Mele" />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-4">
        {/* Statistics Cards */}
        {stats && (
          <div className="grid grid-cols-3 gap-3">
            <Card className="text-center">
              <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
              <div className="text-xs text-gray-600">Total</div>
            </Card>
            <Card className="text-center">
              <div className="text-2xl font-bold text-green-600">{stats.active}</div>
              <div className="text-xs text-gray-600">Active</div>
            </Card>
            <Card className="text-center">
              <div className="text-2xl font-bold text-purple-600">{stats.completionRate}%</div>
              <div className="text-xs text-gray-600">Rata Finalizare</div>
            </Card>
          </div>
        )}

        {/* Goal Templates - Personalized suggestions */}
        {(() => {
          const templates = getGoalTemplates();
          if (templates.length === 0) return null;

          return (
            <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl p-5 text-white shadow-lg">
              <div className="flex items-start gap-3 mb-4">
                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                  <Sparkles className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-1">Obiective Recomandate Pentru Tine</h3>
                  <p className="text-purple-100 text-sm">
                    Sugestii personalizate bazate pe obiectivul tău de sănătate
                  </p>
                </div>
              </div>
              
              <div className="space-y-3">
                {templates.map((template, index) => {
                  const goalTypeInfo = GOAL_TYPES[template.goalType as keyof typeof GOAL_TYPES];
                  const IconComponent = goalTypeInfo?.icon || Target;
                  
                  return (
                    <button 
                      key={index}
                      onClick={() => {
                        setSelectedTemplate(template);
                        setShowCreateModal(true);
                      }}
                      className="w-full text-left bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-colors border border-white/20 cursor-pointer"
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`p-2 rounded-lg ${goalTypeInfo?.color.replace('border-', 'border-2 ')} bg-white/20`}>
                          <IconComponent className="w-4 h-4" />
                        </div>
                        <h4 className="font-semibold flex-1">{template.title}</h4>
                      </div>
                      <p className="text-sm text-purple-100">{template.description}</p>
                    </button>
                  );
                })}
              </div>
              
              <p className="mt-4 text-center text-purple-100 text-sm">
                👆 Apasă pe un template pentru a-l folosi, sau
              </p>
              <button
                onClick={() => {
                  console.log('[GOALS PAGE] Creează Obiectiv Personalizat clicked');
                  setSelectedTemplate(null);
                  setShowCreateModal(true);
                  console.log('[GOALS PAGE] Modal should be opening now');
                }}
                className="mt-2 w-full bg-white text-purple-600 py-2.5 rounded-xl font-medium hover:bg-purple-50 transition-colors flex items-center justify-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Creează Obiectiv Personalizat
              </button>
            </div>
          );
        })()}

        {/* Filter Tabs */}
        <div className="flex gap-2 bg-white rounded-lg p-1">
          <button
            onClick={() => setFilter('all')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition ${
              filter === 'all'
                ? 'bg-blue-500 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Toate
          </button>
          <button
            onClick={() => setFilter('active')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition ${
              filter === 'active'
                ? 'bg-blue-500 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Active
          </button>
          <button
            onClick={() => setFilter('completed')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition ${
              filter === 'completed'
                ? 'bg-blue-500 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Finalizate
          </button>
        </div>

        {/* Goals List */}
        {goals.length === 0 ? (
          <Card className="text-center py-12">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <Target className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Niciun obiectiv încă
            </h3>
            <p className="text-gray-600 mb-6 max-w-sm mx-auto">
              Setează-ți primul obiectiv și urmărește progresul în timp real
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-blue-800 font-medium shadow-lg transition-all"
            >
              <Plus className="w-5 h-5" />
              Adaugă Obiectiv
            </button>
          </Card>
        ) : (
          <>
            {goals.map((goal) => {
              const goalTypeInfo = GOAL_TYPES[goal.goalType as keyof typeof GOAL_TYPES];
              const IconComponent = goalTypeInfo?.icon || Target;
              const isExpanded = expandedCharts.has(goal.id);
              
              return (
                <Card key={goal.id} className="relative overflow-hidden hover:shadow-lg transition-shadow">
                  {/* Gradient Background Accent */}
                  <div className={`absolute top-0 left-0 w-1 h-full bg-gradient-to-b ${goalTypeInfo?.gradient}`} />
                  
                  {/* Completed Badge */}
                  {goal.isCompleted && (
                    <div className="absolute top-4 right-4">
                      <CheckCircle2 className="w-6 h-6 text-green-600" />
                    </div>
                  )}

                  <div className="pl-4">
                    {/* Header */}
                    <div className="flex items-start gap-4 mb-4">
                      <div className={`p-3 rounded-xl ${goalTypeInfo?.color} border`}>
                        <IconComponent className="w-6 h-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-gray-900 mb-1">{goal.title}</h3>
                        {goal.description && (
                          <p className="text-sm text-gray-600 line-clamp-2">{goal.description}</p>
                        )}
                        <div className="mt-2">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${goalTypeInfo?.color}`}>
                            {goalTypeInfo?.label}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Progress Section */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Progres</span>
                        <span className="text-lg font-bold text-gray-900">{goal.progressPercent}%</span>
                      </div>
                      <div className="relative w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`absolute top-0 left-0 h-full bg-gradient-to-r ${goalTypeInfo?.gradient} transition-all duration-500 ease-out`}
                          style={{ width: `${goal.progressPercent}%` }}
                        />
                      </div>
                    </div>

                    {/* Metrics */}
                    {goal.currentValue !== null && goal.targetValue !== null && (
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="bg-gray-50 rounded-lg p-3">
                          <div className="text-xs text-gray-600 mb-1">Valoare Curentă</div>
                          <div className="text-lg font-bold text-gray-900">
                            {goal.currentValue} <span className="text-sm font-normal text-gray-600">{goal.unit}</span>
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <div className="text-xs text-gray-600 mb-1">Țintă</div>
                          <div className="text-lg font-bold text-gray-900">
                            {goal.targetValue} <span className="text-sm font-normal text-gray-600">{goal.unit}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Deadline */}
                    {goal.targetDate && (
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-4 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        <Calendar className="w-4 h-4 text-amber-600" />
                        <span>Deadline: <strong>{new Date(goal.targetDate).toLocaleDateString('ro-RO')}</strong></span>
                      </div>
                    )}

                    {/* Chart Toggle */}
                    {goal.currentValue !== null && goal.targetValue !== null && (
                      <button
                        onClick={() => {
                          const newExpanded = new Set(expandedCharts);
                          if (isExpanded) {
                            newExpanded.delete(goal.id);
                          } else {
                            newExpanded.add(goal.id);
                          }
                          setExpandedCharts(newExpanded);
                        }}
                        className="w-full flex items-center justify-center gap-2 py-2.5 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm font-medium text-gray-700 transition-colors mb-3"
                      >
                        <TrendingUp className="w-4 h-4" />
                        <span>{isExpanded ? 'Ascunde Grafic' : 'Vezi Grafic Progres'}</span>
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    )}

                    {/* Progress Chart */}
                    {isExpanded && (
                      <div className="mb-4 bg-gradient-to-br from-gray-50 to-white p-4 rounded-lg border border-gray-200">
                        <GoalProgressChart goal={goal} />
                      </div>
                    )}

                    {/* Actions */}
                    {!goal.isCompleted && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            const newValue = prompt(
                              `Valoare curentă (${goal.unit}):`,
                              String(goal.currentValue || '')
                            );
                            if (newValue) handleUpdateProgress(goal.id, parseFloat(newValue));
                          }}
                          className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-2.5 rounded-lg hover:from-blue-700 hover:to-blue-800 font-medium shadow-md transition-all"
                        >
                          <TrendingUp className="w-4 h-4" />
                          Actualizează Progres
                        </button>
                        <button
                          onClick={() => handleDeleteGoal(goal.id)}
                          className="p-2.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 border border-red-200 transition-colors"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </>
        )}

        {/* Floating Add Button */}
        {goals.length > 0 && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="fixed bottom-24 right-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white w-14 h-14 rounded-full shadow-lg hover:shadow-xl hover:scale-110 flex items-center justify-center transition-all z-10"
          >
            <Plus className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* Create Goal Modal */}
      {showCreateModal && (
        <CreateGoalModal
          onClose={() => {
            setShowCreateModal(false);
            setSelectedTemplate(null); // Reset template when closing
          }}
          onSuccess={() => {
            loadData();
            setSelectedTemplate(null); // Reset template after success
          }}
          template={selectedTemplate}
        />
      )}
    </div>
  );
}
